import {
  AllOrderedProduct,
  AllOrderedProductsOrder,
  AllOrderedQuantitySummary,
} from "@/features/line-sheet-sets/hooks/use-get-boutique-all-ordered-products";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";
import { calcPrice } from "@/features/line-sheet-sets/helpers/use-inflated-tabs";

// 부티크가 바꾸는 확정 가격조정 — 방식과 %만 고른다(IVA 적용 여부·계산식은 저장된 값을 이어받는다)
export type PriceAdjustmentDraft = Pick<PriceAdjustedBy, "by" | "value"> & {
  // 입력칸에 친 그대로의 글자 — '12.' 처럼 소수점까지만 친 상태를 숫자로 바꾸면 점이 사라진다
  input?: string;
};

// % 입력값 정리 — 숫자와 소수점 하나만 남긴다('12.5.3' → '12.53', '1a2' → '12')
export function sanitizePercentInput(raw: string) {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  return dot === -1
    ? cleaned
    : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
}

/**
 * 확정 가격조정을 바꿨을 때의 확정 단가 미리보기. 서버(apply_confirmed_price_adjustments)와
 * 오더시트 화면(agency-order-sheet-edit-confirm-info)과 같은 규칙이다:
 * 기준가는 그대로 쓰되 방식을 바꾸면 소매가↔도매가로 갈아타고, 없으면 방식에 맞는 가격을 쓴다.
 * 기준가를 정할 수 없으면 지금 확정 단가를 그대로 보여준다.
 */
export function previewConfirmedPrice(
  product: AllOrderedProduct,
  draft: PriceAdjustmentDraft,
  order: Pick<
    AllOrderedProductsOrder,
    "currency" | "commission" | "commissionType"
  >
): number | null {
  const current = product.orderPrice?.priceAdjustedBy ?? null;
  const cost = product.costPrice?.amount || 0;
  const retail = product.retailPrice?.amount || 0;
  let base = product.orderPrice?.priceWithoutCondition?.amount || 0;
  if (current && draft.by !== current.by) {
    if (draft.by === "MARKUP" && base === retail) {
      base = cost || retail;
    } else if (draft.by === "DISCOUNT" && base === cost) {
      base = retail || cost;
    }
  }
  if (!base) {
    base = draft.by === "MARKUP" ? cost : retail;
  }
  if (!base) {
    return product.orderPrice?.price?.amount ?? null;
  }
  const basePrice = { amount: base, currency: order.currency };
  const price = calcPrice(
    {
      by: draft.by,
      value: draft.value,
      applyIva: current?.applyIva,
      ivaValue: current?.ivaValue,
    },
    basePrice,
    basePrice,
    null,
    { commission: order.commission, commissionType: order.commissionType }
  );
  return price?.amount ?? null;
}

export interface AllOrderedProductFilter {
  // null 이면 세트 전체
  lineSheetId: number | null;
  query: string;
}

export interface AllOrderedProductTotal {
  quantity: number;
  // 옵션이 하나도 확정되지 않았으면 null(미확정) — 0 은 '0개로 확정'이라 구분한다
  confirmedQuantity: number | null;
}

const UUID_SUFFIX =
  /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// 업로드 때 파일명 뒤에 붙은 UUID 는 화면에서 뗀다(BOTTEGA VENETA_4bb55aac-… → BOTTEGA VENETA)
export function displayLineSheetName(name: string | null | undefined) {
  return (name ?? "").replace(UUID_SUFFIX, "");
}

export function sumProduct(product: AllOrderedProduct): AllOrderedProductTotal {
  return product.orderQuantityWithOptionList.reduce<AllOrderedProductTotal>(
    (total, option) => ({
      quantity: total.quantity + option.quantity,
      confirmedQuantity:
        option.confirmedQuantity === null
          ? total.confirmedQuantity
          : (total.confirmedQuantity ?? 0) + option.confirmedQuantity,
    }),
    { quantity: 0, confirmedQuantity: null }
  );
}

function normalize(value: string | null | undefined) {
  return (value ?? "").toLowerCase().replace(/[\s\-_.]/g, "");
}

export function filterProducts(
  products: AllOrderedProduct[],
  { lineSheetId, query }: AllOrderedProductFilter
) {
  const needle = normalize(query);
  return products.filter((product) => {
    if (lineSheetId !== null && product.lineSheetId !== lineSheetId) {
      return false;
    }
    if (!needle) {
      return true;
    }
    // SKU 는 구분자가 제각각이라(BFUSW035-730 / BFUSW035 730) 구분자를 떼고 비교한다
    return [
      product.brandName,
      product.modelNumber,
      product.sku,
      product.name,
      product.colorName,
    ].some((field) => normalize(field).includes(needle));
  });
}

export function summarize(
  products: AllOrderedProduct[]
): AllOrderedQuantitySummary {
  return products.reduce<AllOrderedQuantitySummary>(
    (summary, product) => {
      const total = sumProduct(product);
      return {
        quantity: summary.quantity + total.quantity,
        confirmedQuantity:
          summary.confirmedQuantity + (total.confirmedQuantity ?? 0),
      };
    },
    { quantity: 0, confirmedQuantity: 0 }
  );
}
